import { useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { classNames } from '@framework/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePopupRef } from '@framework/hooks';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { faCircle, faSquare } from '@fortawesome/free-regular-svg-icons';
import { StickyZoneShape } from '../../domain';

type Props = {
  onChangeShape(shape: StickyZoneShape): void;
  onOpenSubMenu(menuType: string): void;
};

export const ZoneShapeMenuButton = forwardRef<
  {
    closePopup: () => void;
  },
  Props
>(({ onChangeShape, onOpenSubMenu }, ref) => {
  const { t } = useTranslation();
  const { isOpen, popupRef, handleToggle, handleClose } = usePopupRef<HTMLDivElement>(false);

  useImperativeHandle(ref, () => ({
    closePopup: () => handleClose(),
  }));

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      onOpenSubMenu('zoneShape');
      handleToggle();
    },
    [handleToggle, onOpenSubMenu]
  );

  return (
    <div className="relative hover:bg-gray-200">
      <button
        // data-right-click-menu は親 item をクリックしても閉じないようにするためのマーカー
        data-right-click-menu
        className={classNames('flex w-full cursor-pointer px-8 py-4')}
        onClick={handleClick}
      >
        <div className="inline-block pl-4 pr-6">{t('canvas.menu.zone.shape')}</div>
        <FontAwesomeIcon icon={faAngleRight} className="ml-auto" />
      </button>
      <div
        className={classNames(
          'absolute left-full top-0 z-50 w-max rounded-lg bg-white py-4 shadow-2xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        <button
          className="flex w-full py-6 pl-8 pr-12 hover:bg-gray-300"
          onClick={() => onChangeShape(StickyZoneShape.Rectangle)}
        >
          <FontAwesomeIcon icon={faSquare} />
          <span className="flex-none pl-4">{t('canvas.zone.shape.rectangle')}</span>
        </button>
        <button
          className="flex w-full py-6 pl-8 pr-12 hover:bg-gray-300"
          onClick={() => onChangeShape(StickyZoneShape.Circle)}
        >
          <FontAwesomeIcon icon={faCircle} />
          <span className="flex-none pl-4">{t('canvas.zone.shape.circle')}</span>
        </button>
      </div>
    </div>
  );
});

ZoneShapeMenuButton.displayName = 'ZoneShapeMenuButton';
